import { useMemo } from "react";
import useFlag, { FLAGS } from "@coworker/app/src/hooks/useFlag";
import { useMediaItemsWithTasks } from "@coworker/apprestructured/src/media-maintenance/components/MediaTasksSection/hooks/useMediaItemsWithTasks";
import { useMediaTaskCountsV2 } from "@coworker/apprestructured/src/media-maintenance/hooks/useMediaTaskCountsV2";
import { MEDIA_TASK_TYPE_NAMES } from "@coworker/apprestructured/src/task-management/constants/TaskTypeNames";
import { useAllTaskTypes } from "@coworker/apprestructured/src/task-management/services/useTaskTypes";

export interface MediaV2TaskLookup {
  /** Keyed by item number (productArticleId). */
  byItem: Set<string>;
  /** Keyed by `${articleId}|${itemNo}`. */
  byArticle: Set<string>;
  /** True when the v2 lookup is active; false when the feature flag is off. */
  enabled: boolean;
}

export function v2ArticleKey(articleId: string, itemNo: string) {
  return `${articleId}|${itemNo}`;
}

const EMPTY_LOOKUP: MediaV2TaskLookup = {
  byItem: new Set<string>(),
  byArticle: new Set<string>(),
  enabled: false,
};

/**
 * Task type ids for the media maintenance task types, used to scope every v2
 * media query.
 */
function useMediaTaskTypeIds() {
  const { data: taskTypes = [] } = useAllTaskTypes();

  return useMemo(
    () =>
      taskTypes
        .filter((taskType) =>
          (MEDIA_TASK_TYPE_NAMES as readonly string[]).includes(taskType.name)
        )
        .map((taskType) => taskType.id),
    [taskTypes]
  );
}

/**
 * Ongoing v2 task count per media id, for the media overview.
 */
export function useMediaV2TaskCounts(): Map<string, number> {
  const enableMediaV2Tasks = useFlag(FLAGS.ENABLE_MEDIA_V2_TASKS);
  const mediaTaskTypeIds = useMediaTaskTypeIds();

  const { data: v2TaskCountsMap = new Map<string, number>() } =
    useMediaTaskCountsV2(enableMediaV2Tasks ? mediaTaskTypeIds : []);

  return v2TaskCountsMap;
}

/**
 * Single source of truth for "does this article have an ongoing v2 task in this
 * media?".
 *
 * Both lookups are derived from GET /tasks-v2/items, which returns
 * { article_id, item_no } pairs scoped to one media:
 *  - byItem    - loose match, used by the aggregated article list where rows are
 *                aggregated by item number.
 *  - byArticle - exact match, used by the group view where the same item can
 *                appear as several article rows across groups.
 *
 * The underlying query key is shared with every other useMediaItemsWithTasks
 * caller, so repeated use across a screen costs no extra requests.
 */
export function useMediaV2TaskLookup(
  mediaId: string | undefined
): MediaV2TaskLookup {
  const enableMediaV2Tasks = useFlag(FLAGS.ENABLE_MEDIA_V2_TASKS);
  const mediaTaskTypeIds = useMediaTaskTypeIds();

  const { data: v2TaskItems = [] } = useMediaItemsWithTasks(
    enableMediaV2Tasks ? mediaId : undefined,
    mediaTaskTypeIds
  );

  return useMemo(() => {
    if (!enableMediaV2Tasks) {
      return EMPTY_LOOKUP;
    }

    const byItem = new Set<string>();
    const byArticle = new Set<string>();

    for (const { article_id, item_no } of v2TaskItems) {
      byItem.add(item_no);
      byArticle.add(v2ArticleKey(article_id, item_no));
    }

    return { byItem, byArticle, enabled: true };
  }, [enableMediaV2Tasks, v2TaskItems]);
}
